import { validators } from 'investira.sdk';

import {
  isFundoNaoListadoDisplayMode,
  isRendaFixaDisplayMode
} from '../../../../components/organisms/ativoDisplayMode';

export const ATIVO_HIGHLIGHT_GROUP = {
  indicadores: 'indicadores',
  benchmark: 'benchmark',
  fundoInfo: 'fundoInfo',
  rendaFixaInfo: 'rendaFixaInfo'
};

const wColumnsByVisibleGroups = {
  1: {
    md: [6],
    lg: [12]
  },
  2: {
    md: [2, 4],
    lg: [4, 8]
  },
  3: {
    md: [2, 4, 6],
    lg: [3, 4, 5]
  }
};

/**
 * Indica se o payload possui um valor utilizável, preservando zero.
 *
 * @param {*} pValue Valor avaliado.
 * @returns {boolean}
 */
function pvHasValue(pValue) {
  return !validators.isNull(pValue);
}

/**
 * Normaliza os dados e identifica os grupos disponíveis para o destaque.
 *
 * @param {Object} pAtivo Dados cadastrais do ativo.
 * @param {Object} pAtivoAvaliacao Dados de avaliação do ativo.
 * @param {string} pDisplayMode Família de apresentação do ativo.
 * @returns {Object}
 */
export function getAtivoHighlightData(pAtivo = {}, pAtivoAvaliacao = {}, pDisplayMode) {
  const xAvaliacao = {
    ...pAtivoAvaliacao?.avaliacao,
    benchmark_indicador_id:
      pAtivoAvaliacao?.avaliacao?.benchmark_indicador_id || pAtivo?.fundo?.benchmark_indicador_id
  };
  const xRetornoValue = xAvaliacao.retorno;
  const xSharpeValue = xAvaliacao.sharpe;
  const xHasRetorno = pvHasValue(xRetornoValue);
  const xHasSharpe = pvHasValue(xSharpeValue);
  const xHasIndicadores = xHasRetorno || xHasSharpe;
  const xHasBenchmarkData = [
    xAvaliacao.benchmark_indicador_id,
    xAvaliacao.perc_dias_acima_benchmark,
    xAvaliacao.perc_meses_acima_benchmark,
    xAvaliacao.amostragem
  ].some(pvHasValue);
  const xFundoInfoData = pAtivo?.fundo || {};
  const xHasFundoInfo =
    isFundoNaoListadoDisplayMode(pDisplayMode) &&
    [
      xFundoInfoData.publico_alvo,
      xFundoInfoData.aplicacao_min,
      xFundoInfoData.cotizacao?.dias_resgate,
      xFundoInfoData.resgate?.dias_pagamento,
      xFundoInfoData.taxas?.adm,
      xFundoInfoData.performance?.taxa
    ].some(pvHasValue);
  const xHasRendaFixaInfo = isRendaFixaDisplayMode(pDisplayMode);
  const xVisibleGroups = [
    {
      id: ATIVO_HIGHLIGHT_GROUP.indicadores,
      visible: xHasIndicadores
    },
    {
      id: ATIVO_HIGHLIGHT_GROUP.benchmark,
      visible: xHasBenchmarkData
    },
    {
      id: ATIVO_HIGHLIGHT_GROUP.fundoInfo,
      visible: xHasFundoInfo
    },
    {
      id: ATIVO_HIGHLIGHT_GROUP.rendaFixaInfo,
      visible: xHasRendaFixaInfo
    }
  ]
    .filter(pGroup => pGroup.visible)
    .map(pGroup => pGroup.id);

  return {
    avaliacao: xAvaliacao,
    fundoInfoData: xFundoInfoData,
    hasBenchmarkData: xHasBenchmarkData,
    hasFundoInfo: xHasFundoInfo,
    hasIndicadores: xHasIndicadores,
    hasRendaFixaInfo: xHasRendaFixaInfo,
    hasRetorno: xHasRetorno,
    hasSharpe: xHasSharpe,
    retornoValue: xRetornoValue,
    sharpeValue: xSharpeValue,
    visibleGroups: xVisibleGroups
  };
}

/**
 * Distribui os grupos visíveis nas colunas do grid conforme sua ordem visual.
 *
 * @param {string[]} pVisibleGroups Identificadores dos grupos visíveis.
 * @returns {Object}
 */
export function getAtivoHighlightColumns(pVisibleGroups = []) {
  const xColumns = {};
  const xLayout = wColumnsByVisibleGroups[pVisibleGroups.length];

  if (!xLayout) {
    return xColumns;
  }

  pVisibleGroups.forEach((pGroup, pIndex) => {
    xColumns[pGroup] = {
      md: `span ${xLayout.md[pIndex]}`,
      lg: `span ${xLayout.lg[pIndex]}`
    };
  });

  return xColumns;
}

/**
 * Converte o Sharpe percentual do payload para a escala exibida pelo card.
 *
 * @param {number|string} pValue Valor recebido no payload.
 * @returns {number}
 */
export function normalizeAtivoHighlightSharpe(pValue) {
  return Number(pValue) / 100;
}
